import type { MetricIdentity } from "@/metrics/catalog"

import { resolveMetricDisplayName } from "@/utils/formatting/format-metric-display-results"

import { GROUP_BY_TYPE, type GroupByType } from "../types/chart-types"
import type { FilterType } from "../types/core"
import { getMetadataGroupDisplayName } from "./chart/splitKeys"
import type { MetricValue } from "./chartShared"

interface TableMetricCell {
  count?: number
  value?: number
}

export interface TableColumnDefinition {
  key: string
  label: string
  sortable: boolean
}

export const resolveTableMetricIds = ({
  filters,
  metricId,
  metricIds,
}: {
  filters?: FilterType
  metricId?: string
  metricIds?: string[]
}): string[] => {
  if (filters && "metricIds" in filters) {
    return filters.metricIds ?? []
  }

  return metricIds || (metricId ? [metricId] : [])
}

interface TableRowDataBase {
  count?: number
  metricCells: Record<string, TableMetricCell | undefined>
}

export interface TableRowData extends TableRowDataBase {
  test_set_id?: string
  testSetName?: string
  run_agent_id?: string
  agentName?: string
  persona_id?: string
  personaName?: string
  run_template_name?: string
  templateName?: string
  mutation_id?: string
  mutationName?: string
  assignee?: string
  metadataValue?: string
}

interface TableDimensionConfig {
  idKey: keyof TableRowData
  nameKey: keyof TableRowData
  getId: (metricValue: MetricValue) => string
  getName: (metricValue: MetricValue, id: string) => string
}

interface TableMetricQueryPartition {
  metricIds: string[]
  outputType: "float" | "string"
}

const UNASSIGNED_ASSIGNEE_KEY = "__unassigned__"

const ID_TO_NAME_KEY: Record<string, keyof TableRowData> = {
  run_agent_id: "agentName",
  mutation_id: "mutationName",
  test_set_id: "testSetName",
  persona_id: "personaName",
  run_template_name: "templateName",
  assignee: "assignee",
}

const DIMENSION_LABELS: Record<string, { key: string; label: string }> = {
  agent: { key: "run_agent_id", label: "Agent" },
  mutation: { key: "mutation_id", label: "Mutation" },
  test_set: { key: "test_set_id", label: "Test Set" },
  persona: { key: "persona_id", label: "Persona" },
  template: { key: "run_template_name", label: "Template" },
  assignee: { key: "assignee", label: "Assignee" },
}

const hasSelfReferentialTestSetName = ({
  metricValue,
  resolvedTestSetName,
}: {
  metricValue: MetricValue
  resolvedTestSetName?: string
}) => {
  if (resolvedTestSetName) {
    return false
  }

  return (
    typeof metricValue.test_set_id === "string" &&
    metricValue.test_set_id.length > 0 &&
    metricValue.testSetName === metricValue.test_set_id
  )
}

const getDimensionConfig = (
  groupBy: GroupByType | undefined,
  groupByMetadataKey: string | undefined,
  testSetNameMap: Map<string, string>,
  personaNameMap: Map<string, string>,
): TableDimensionConfig => {
  const configs: Record<string, TableDimensionConfig> = {
    metadata: {
      idKey: "metadataValue",
      nameKey: "metadataValue",
      getId: (metricValue) => metricValue.metadataValue || "",
      getName: (metricValue) => metricValue.metadataValue || "",
    },
    agent: {
      idKey: "run_agent_id",
      nameKey: "agentName",
      getId: (metricValue) => metricValue.run_agent_id || "",
      getName: (metricValue) =>
        metricValue.agentName || metricValue.run_agent_id || "",
    },
    mutation: {
      idKey: "mutation_id",
      nameKey: "mutationName",
      getId: (metricValue) => metricValue.mutation_id || "",
      getName: (metricValue) =>
        metricValue.mutationName || metricValue.mutation_id || "",
    },
    test_set: {
      idKey: "test_set_id",
      nameKey: "testSetName",
      getId: (metricValue) => metricValue.test_set_id || "",
      getName: (metricValue, id) =>
        testSetNameMap.get(id) || metricValue.testSetName || id,
    },
    persona: {
      idKey: "persona_id",
      nameKey: "personaName",
      getId: (metricValue) => metricValue.persona_id || "",
      getName: (metricValue, id) =>
        personaNameMap.get(id) || metricValue.personaName || id,
    },
    template: {
      idKey: "run_template_name",
      nameKey: "templateName",
      getId: (metricValue) => metricValue.run_template_name || "",
      getName: (metricValue) =>
        metricValue.templateName || metricValue.run_template_name || "",
    },
    assignee: {
      idKey: "assignee",
      nameKey: "assignee",
      getId: (metricValue) => metricValue.assignee || UNASSIGNED_ASSIGNEE_KEY,
      getName: (metricValue) => metricValue.assignee || "Unassigned",
    },
  }

  return (
    configs[groupByMetadataKey ? "metadata" : groupBy || GROUP_BY_TYPE.AGENT] ||
    configs.agent
  )
}

export const resolveTableMetricOutputType = (
  metric: MetricIdentity | undefined,
): "float" | "string" => {
  if (metric?.metric_manager === "manager_llm_binary") {
    return "string"
  }

  return metric?.output_type === "string" ? "string" : "float"
}

export const partitionMetricIdsByOutputType = ({
  metricIds,
  metricRecords,
}: {
  metricIds: string[]
  metricRecords: Record<string, MetricIdentity> | null
}): TableMetricQueryPartition[] => {
  if (!metricRecords) {
    return []
  }

  const partitions = new Map<"float" | "string", string[]>()

  for (const metricId of metricIds) {
    const outputType = resolveTableMetricOutputType(metricRecords[metricId])
    const existingMetricIds = partitions.get(outputType) ?? []
    existingMetricIds.push(metricId)
    partitions.set(outputType, existingMetricIds)
  }

  return Array.from(partitions.entries()).map(([outputType, partitionIds]) => ({
    outputType,
    metricIds: partitionIds,
  }))
}

export const buildTableRows = ({
  groupBy,
  groupByMetadataKey,
  metricIds,
  metricValues,
  personaNameMap,
  testSetNameMap,
}: {
  groupBy?: GroupByType
  groupByMetadataKey?: string
  metricIds: string[]
  metricValues: MetricValue[]
  personaNameMap: Map<string, string>
  testSetNameMap: Map<string, string>
}): TableRowData[] => {
  const isSingleMetric = metricIds.length <= 1

  if (!groupBy && !groupByMetadataKey) {
    const metricCells: Record<string, TableMetricCell | undefined> = {}

    metricValues.forEach((metricValue) => {
      const metricId = metricValue.metric_id || metricValue.metricId
      if (!metricId) {
        return
      }

      metricCells[metricId] = {
        value:
          metricValue.value != null ? Number(metricValue.value) : undefined,
        count:
          metricValue.count != null ? Number(metricValue.count) : undefined,
      }
    })

    const row: TableRowData = { metricCells }

    if (isSingleMetric) {
      const firstMetricId = metricIds[0]
      row.count = metricCells[firstMetricId]?.count ?? 0
    }

    return [row]
  }

  const dimension = getDimensionConfig(
    groupBy,
    groupByMetadataKey,
    testSetNameMap,
    personaNameMap,
  )
  const groupMap = new Map<string, TableRowData>()

  metricValues.forEach((metricValue) => {
    const id = dimension.getId(metricValue)
    if (!id) {
      return
    }

    if (
      groupBy === GROUP_BY_TYPE.TEST_SET &&
      hasSelfReferentialTestSetName({
        metricValue,
        resolvedTestSetName: testSetNameMap.get(id),
      })
    ) {
      return
    }

    const metricId = metricValue.metric_id || metricValue.metricId
    if (!metricId) {
      return
    }

    const existingRow =
      groupMap.get(id) ??
      ({
        [dimension.idKey]: id,
        [dimension.nameKey]: dimension.getName(metricValue, id),
        metricCells: {},
      } as TableRowData)

    existingRow.metricCells[metricId] = {
      value: metricValue.value != null ? Number(metricValue.value) : undefined,
      count: metricValue.count != null ? Number(metricValue.count) : undefined,
    }

    if (isSingleMetric) {
      existingRow.count = existingRow.metricCells[metricId]?.count ?? 0
    }

    groupMap.set(id, existingRow)
  })

  return Array.from(groupMap.values())
}

export const buildTableColumns = ({
  groupBy,
  groupByMetadataKey,
  metricIds,
  metricRecords,
}: {
  groupBy?: GroupByType
  groupByMetadataKey?: string
  metricIds: string[]
  metricRecords: Record<string, MetricIdentity> | null
}): TableColumnDefinition[] => {
  const columns: TableColumnDefinition[] = []
  const isSingleMetric = metricIds.length <= 1

  if (groupBy || groupByMetadataKey) {
    const dimension = groupByMetadataKey
      ? { key: "metadataValue", label: groupByMetadataKey }
      : DIMENSION_LABELS[groupBy!] || DIMENSION_LABELS.agent
    columns.push({
      key: dimension.key,
      label: dimension.label,
      sortable: true,
    })
  }

  if (isSingleMetric) {
    columns.push({
      key: "count",
      label: "# of Conversations",
      sortable: true,
    })
  }

  metricIds.forEach((metricId) => {
    const metric = metricRecords?.[metricId]
    if (!metric) {
      return
    }

    columns.push({
      key: metricId,
      label: resolveMetricDisplayName(
        metric.metric_display_name,
        metric.metric_name || metricId,
      ),
      sortable: true,
    })
  })

  return columns
}

export const getTableCellSortValue = (
  row: TableRowData,
  columnKey: string,
): number | string | null => {
  const rawRow = row as unknown as Record<string, string | number | undefined>

  if (columnKey === "count") {
    return row.count ?? 0
  }

  if (columnKey === "metadataValue") {
    return row.metadataValue
      ? getMetadataGroupDisplayName(row.metadataValue)
      : null
  }

  const displayNameKey = ID_TO_NAME_KEY[columnKey]
  if (displayNameKey) {
    return rawRow[displayNameKey] ?? rawRow[columnKey] ?? null
  }

  return row.metricCells[columnKey]?.value ?? null
}

export const formatMetricCellValue = ({
  cell,
  showDenominator,
}: {
  cell?: TableMetricCell
  showDenominator: boolean
}): string | null => {
  if (cell?.value == null) {
    return null
  }

  const formattedValue = `${(cell.value * 100).toFixed(0)}%`

  if (!showDenominator) {
    return formattedValue
  }

  if (cell.count == null) {
    return formattedValue
  }

  return `${formattedValue} (n=${cell.count})`
}

const serializeMetricCells = (row: TableRowData) =>
  Object.entries(row.metricCells)
    .sort(([leftMetricId], [rightMetricId]) =>
      leftMetricId.localeCompare(rightMetricId),
    )
    .map(([metricId, cell]) => ({
      count: cell?.count ?? null,
      metricId,
      value: cell?.value ?? null,
    }))

export const buildTableExportDataKey = ({
  columns,
  dateRange,
  rows,
}: {
  columns: TableColumnDefinition[]
  dateRange: { end: Date; start: Date }
  rows: TableRowData[]
}) =>
  JSON.stringify({
    columns: columns.map((column) => ({
      key: column.key,
      label: column.label,
    })),
    end: dateRange.end.getTime(),
    rows: rows.map((row) => ({
      agentName: row.agentName ?? null,
      assignee: row.assignee ?? null,
      metadataValue: row.metadataValue ?? null,
      count: row.count ?? null,
      metricCells: serializeMetricCells(row),
      mutation_id: row.mutation_id ?? null,
      mutationName: row.mutationName ?? null,
      persona_id: row.persona_id ?? null,
      personaName: row.personaName ?? null,
      run_agent_id: row.run_agent_id ?? null,
      run_template_name: row.run_template_name ?? null,
      templateName: row.templateName ?? null,
      test_set_id: row.test_set_id ?? null,
      testSetName: row.testSetName ?? null,
    })),
    start: dateRange.start.getTime(),
  })

const TABLE_EXPORT_ERROR_KEY = "__table_export_error__"

export const resolveTableExportState = ({
  columns,
  dateRange,
  error,
  rows,
}: {
  columns: TableColumnDefinition[]
  dateRange: { end: Date; start: Date }
  error: string | null
  rows: TableRowData[]
}) => {
  if (error) {
    return {
      exportDataKey: TABLE_EXPORT_ERROR_KEY,
      tableData: null,
    }
  }

  return {
    exportDataKey: buildTableExportDataKey({
      columns,
      dateRange,
      rows,
    }),
    tableData:
      rows.length > 0 && columns.length > 0
        ? {
            columns,
            rows,
          }
        : null,
  }
}
